import prettyBytes from 'pretty-bytes';

export const formatCompactNumber = (value: number) => {
    if (Number.isNaN(value)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
};

export const rsaKeyValidator = (key: string) => {
    // Remove any leading/trailing whitespace
    const trimmedKey = key.trim();

    // Check for PEM format: RSA PUBLIC KEY
    const rsaPublicKeyRegex = /^-----BEGIN RSA PUBLIC KEY-----[\s\S]*?-----END RSA PUBLIC KEY-----$/;

    // Check for PEM format: PUBLIC KEY
    const publicKeyRegex = /^-----BEGIN PUBLIC KEY-----[\s\S]*?-----END PUBLIC KEY-----$/;

    // Check for OpenSSH format: ssh-rsa
    const sshRsaRegex = /^ssh-rsa\s+[A-Za-z0-9+/]+[=]*\s+[^\s]+$/;

    return rsaPublicKeyRegex.test(trimmedKey) || publicKeyRegex.test(trimmedKey) || sshRsaRegex.test(trimmedKey);
};

export const ipRangeValidator = (ipRange: string) => {
    const ipRangeRegex = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2})$/;
    return ipRangeRegex.test(ipRange);
};

/** Characters invalid in file names (Windows and Unix). Regex patterns (e.g. ./^...$/) are allowed. */
export const INVALID_FILENAME_CHARS_REGEX = /[\x00-\x1f\\/:*?"<>|]/;

/**
 * MDaaS filename.filters: user-defined regex for filenames must match this pattern.
 * @see MDaaS Allowlist Configurations - filename.filters name field validation
 */
export const FILENAME_FILTER_VALIDATION_REGEX = /^[^.]{1,256}\.[a-zA-Z0-9]+$/;

export const isRegexPattern = (value: string) =>
    (value.startsWith('./') && value.length > 2 && value.lastIndexOf('/') > 2) ||
    FILENAME_FILTER_VALIDATION_REGEX.test(value);

export const isInvalidFilename = (value: string) => {
    if (!value.trim()) return true;
    if (isRegexPattern(value)) return false;
    return INVALID_FILENAME_CHARS_REGEX.test(value);
};

const coerceToFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (Object.prototype.toString.call(value) === '[object Number]') {
        const unboxed = Number(value);
        return Number.isFinite(unboxed) ? unboxed : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0) return null;
        const num = Number(trimmed);
        return Number.isFinite(num) ? num : null;
    }

    try {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    } catch {
        return null;
    }
};

export const formatFileSize = (bytes: unknown): string => {
    if (typeof bytes === 'bigint') return prettyBytes(bytes);
    const num = coerceToFiniteNumber(bytes);
    if (num === null) {
        return '-';
    }
    return prettyBytes(num);
};

export const formatNumber = (value: number): string => {
    if (value >= 1000) {
        return `${(value / 1000).toLocaleString()}k`;
    }
    return value.toLocaleString();
};
export const cveRegex = /^(CVE-)?[1-2]\d{3}-\d{4,8}$/i;

export const toCamelCase = (str: string) => {
    str = str.toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const toCamelCaseAll = (str: string) => {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const truncateMiddle = (text: string, frontChars: number = 14, backChars: number = 10): string => {
    const maxLength = frontChars + backChars + 1;
    if (!text || text.length <= maxLength) return text;

    return text.slice(0, frontChars) + '....' + text.slice(-backChars);
};

export const isValidOAuthClientName = (value: string) => {
    const CLIENT_NAME_REGEX = /^[\w\s+=,.@-]{1,128}$/;

    return CLIENT_NAME_REGEX.test(value);
};
